/**
 * Phase 25: ChunkErrorBoundary
 *
 * With route-based code-splitting, each page is a separate JS chunk loaded on
 * demand. Two failure modes need handling:
 *
 *  1. Transient network failure while fetching a chunk.
 *  2. Stale deploy: a user has the app open, a new version is deployed, and
 *     their already-loaded index references chunk hashes that no longer exist
 *     on the server. Their next navigation triggers a dynamic import that
 *     404s.
 *
 * Either way React throws during render of the lazy component. Without a
 * boundary that surfaces as a white screen. This boundary catches the failure
 * and offers a reload — which fetches the current index.html and the current
 * chunk hashes, resolving the stale-deploy case.
 *
 * We deliberately keep this dependency-free and class-based: error boundaries
 * must be class components, and this one must itself never fail to load.
 */

import { Component, type ReactNode, type ErrorInfo } from 'react';
import { looksLikeChunkError, reloadForChunkError } from '@/lib/chunkReload';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
}

export class ChunkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error: unknown): State {
    return { hasError: true, isChunkError: looksLikeChunkError(error) };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    if (looksLikeChunkError(error)) {
      // Stale deploy: auto-reload once (rate-limited) to fetch current chunks.
      // If a reload is triggered the page navigates away before the fallback
      // is seen; if rate-limited, the manual "Reload" fallback is shown.
      reloadForChunkError();
      return;
    }
    // Log for diagnostics.
    console.error('ChunkErrorBoundary caught:', error, info.componentStack);
  }

  private handleReload = (): void => {
    // Full reload — fetches the current index.html and current chunk hashes.
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    const { isChunkError } = this.state;

    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <h2 className="text-lg font-bold text-ink-primary mb-2">
            {isChunkError ? 'A newer version is available' : 'Something went wrong'}
          </h2>
          <p className="text-sm text-ink-secondary mb-5">
            {isChunkError
              ? 'This page could not finish loading — usually because the app was updated since you opened it. Reloading will pick up the latest version.'
              : 'An unexpected error occurred while loading this page. Reloading often resolves it.'}
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-brand-navy text-white text-sm font-semibold hover:bg-brand-navy/90 transition-colors"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
